import React, { Component } from 'react';
import { connect } from 'react-redux';
import { Input, IncrementDecrement } from '@trp-rps-rps-shared-web/rps-shared-web-components';
import * as tokens from '@trp-rps-rps-shared-web/rps-shared-web-styles';
import styled from 'styled-components';
import InputTilePopoverCard from './InputTilePopoverCard';
import { inputTilesActions, socialSecurityActions } from '../../redux/root-action';
import { getSpouseSocialSecurityAdvice, getParticipantSocialSecurityAdvice } from '../../selectors';
import {
  formatAmountWithoutDollar,
  getFloatAmount,
  formatDollarAmountChange,
  getPreciseValue,
} from '../../utils/helpers';
import { analyticsTracking } from '../../utils/constants';

const TileHeader = styled.div`
  font-family: Nimbus Sans Bold;
  font-size: 20px;
  letter-spacing: -0.3px;
  color: ${tokens.ColorGrey900};
`;

const TileSubheader = styled.div`
  font-family: Nimbus Sans Regular;
  font-size: 16px;
  line-height: 22px;
  color: ${tokens.ColorGrey700};
  padding-bottom: 16px;
  letter-spacing: 0.4px;
  border-bottom: 1px solid ${tokens.ColorWhite200};
  margin-bottom: 24px;
`;

const TileContentSection = styled.div`
  display: grid;
  display: -ms-grid;
  grid-template-columns: 1fr 2fr 2fr;
  -ms-grid-columns: 1fr 2fr 2fr;
  grid-row-gap: 0px;
  grid-column-gap: 16px;
  align-items: center;
  justify-items: left;
  @media (max-width: 767px) {
    grid-row-gap: 8px;
  }
  div:nth-child(5) {
    -ms-grid-row: 2;
    -ms-grid-column: 2;
  }
  div:nth-child(6) {
    -ms-grid-row: 2;
    -ms-grid-column: 3;
    @media all and (-ms-high-contrast: none), (-ms-high-contrast: active) {
      margin-left: -24px;
    }
  }
  &:nth-child(2) {
    div:nth-child(1) {
      -ms-grid-row: 1;
      -ms-grid-column: 1;
      }
    }
    div:nth-child(2) {
      -ms-grid-row: 1;
      -ms-grid-column: 2;
      }
    }
    div:nth-child(3) {
      -ms-grid-row: 1;
      -ms-grid-column: 3;
      @media all and (-ms-high-contrast: none), (-ms-high-contrast: active) {
        margin-left: -24px;
        }
      }
    }
  }
`;

const StyledDivider = styled.div`
  border-bottom: 1px solid ${tokens.ColorWhite100};
  margin: 24px 0px;
  @media (max-width: 767px) {
    margin: 32px 0px;
  }
`;

const StyledError = styled.div`
  color: ${tokens.ColorFunctionalRed700};
  margin-top: 24px;
`;

const StyledText = styled.div`
  margin-top: -20px;
  justify-self: left;
  -ms-grid-column: 1;
  -ms-grid-row: 1;
  @media (max-width: 767px) {
    margin-top: -35px;
  }
  @media all and (-ms-high-contrast: none), (-ms-high-contrast: active) {
    padding-top: 36px;
  }
  &:nth-child(4) {
    -ms-grid-row: 2;
  }
`;

const StyledHeader = styled.div`
  font-family: Nimbus Sans Bold;
  font-size: 14px;
  justify-self: center;
  -ms-grid-column: 2;
  -ms-grid-row: 1;
  @media all and (-ms-high-contrast: none), (-ms-high-contrast: active) {
    padding-left: 36px;
  }
`;

const StyledBenefitHeader = styled.div`
  font-family: Nimbus Sans Bold;
  font-size: 14px;
  -ms-grid-row: 1;
  -ms-grid-column: 3;
  @media all and (-ms-high-contrast: none), (-ms-high-contrast: active) {
    margin-left: -24px;
  }
`;

const PARTICIPANT = 'participant',
  SPOUSE = 'spouse',
  INC = 'increment',
  DEC = 'decrement';

class SocialSecurity extends Component<any> {
  props: any;
  state: any;

  isDataValid = () => {
    const {
      socialSecurity: {
        tempScenario: { socialSecurityAmount, spouseSocialSecurityAmount },
      },
      updateSocialSecurityError,
    } = this.props;
    const errorMessage = 'Estimated benefit must be $0–$99,999.99.';
    const socialSecurityAmountError = getFloatAmount(socialSecurityAmount) > 99999.99 ? errorMessage : '';
    const spouseSocialSecurityAmountError = getFloatAmount(spouseSocialSecurityAmount) > 99999.99 ? errorMessage : '';

    updateSocialSecurityError({
      socialSecurityAmountError,
      spouseSocialSecurityAmountError,
    });
    return !socialSecurityAmountError && !spouseSocialSecurityAmountError;
  };

  handleDone = () => {
    const { modelSocialSecurityScenario, hideInputTilePopover } = this.props;
    if (this.isDataValid()) {
      modelSocialSecurityScenario();
      hideInputTilePopover();
    }
  };

  handleCancel = () => {
    const {
      updateSocialSecurityData,
      hideInputTilePopover,
      socialSecurity: { modelledScenario },
    } = this.props;
    updateSocialSecurityData(modelledScenario);
    hideInputTilePopover();
  };

  getSocialSecurityBenefit = (socialSecurityAge: any, user: string) => {
    const { participantSocialSecurityAdvice, spouseSocialSecurityAdvice } = this.props;
    const socialSecurityAdvice = user === PARTICIPANT ? participantSocialSecurityAdvice : spouseSocialSecurityAdvice;

    if (!socialSecurityAdvice || socialSecurityAdvice.length === 0) {
      return null;
    }

    let socialSecurityAmount = 0;
    socialSecurityAdvice.forEach((item: any) => {
      if (item.StartAge === socialSecurityAge) {
        socialSecurityAmount = getPreciseValue(item.MonthlyIncome * 100) / 100;
      }
    });

    return socialSecurityAmount;
  };

  upateSocialSecurityAge = (user: string, type: string) => {
    const {
      socialSecurity: {
        tempScenario: { socialSecurityStartAge, spouseSocialSecurityStartAge },
      },
      updateSocialSecurityData,
    } = this.props;
    return () => {
      const updateValue = type === INC ? 1 : -1;
      switch (user) {
        case PARTICIPANT:
          updateSocialSecurityData({
            socialSecurityStartAge: socialSecurityStartAge + updateValue,
            socialSecurityAmount: this.getSocialSecurityBenefit(socialSecurityStartAge + updateValue, PARTICIPANT),
          });
          break;
        case SPOUSE:
          updateSocialSecurityData({
            spouseSocialSecurityStartAge: spouseSocialSecurityStartAge + updateValue,
            spouseSocialSecurityAmount: this.getSocialSecurityBenefit(
              spouseSocialSecurityStartAge + updateValue,
              SPOUSE,
            ),
          });
          break;
        default:
          break;
      }
    };
  };

  handleSocialSecurityAmountChange = (user: string) => {
    return (e: any) => {
      const {
        updateSocialSecurityData,
        socialSecurity: {
          tempScenario: { socialSecurityAmount, spouseSocialSecurityAmount },
        },
      } = this.props;
      const formattedAmount = formatDollarAmountChange(e.target.value);
      switch (user) {
        case PARTICIPANT:
          updateSocialSecurityData({
            socialSecurityAmount: formattedAmount ? Number(formattedAmount) : socialSecurityAmount,
          });
          break;
        case SPOUSE:
          updateSocialSecurityData({
            spouseSocialSecurityAmount: formattedAmount ? Number(formattedAmount) : spouseSocialSecurityAmount,
          });
          break;
        default:
          break;
      }
    };
  };

  componentDidMount() {
    const {
      updateSocialSecurityData,
      socialSecurity: {
        tempScenario: {
          socialSecurityStartAge,
          spouseSocialSecurityStartAge,
          socialSecurityAmount,
          spouseSocialSecurityAmount,
        },
      },
    } = this.props;

    updateSocialSecurityData({
      socialSecurityAmount: socialSecurityAmount || this.getSocialSecurityBenefit(socialSecurityStartAge, PARTICIPANT),
      spouseSocialSecurityAmount:
        spouseSocialSecurityAmount || this.getSocialSecurityBenefit(spouseSocialSecurityStartAge, SPOUSE),
    });
  }

  render(): JSX.Element {
    const mq = window.matchMedia('(max-width: 767px)');

    const {
      socialSecurity: {
        tempScenario: {
          socialSecurityStartAge,
          spouseSocialSecurityStartAge,
          socialSecurityAmount,
          spouseSocialSecurityAmount,
          hasSpouse,
        },
        error: { socialSecurityAmountError, spouseSocialSecurityAmountError },
      },
    } = this.props;

    return (
      <InputTilePopoverCard
        handleDone={this.handleDone}
        handleCancel={this.handleCancel}
        analyticsId={analyticsTracking.RIXSocialSecurityInputTileDone}
        cancelId={analyticsTracking.RIXSocialSecurityInputTileCancel}
      >
        <TileHeader id={`social-security-title`}>Social Security</TileHeader>
        <TileSubheader id={`social-security-subheader`}>You can begin claiming from age 62-70.</TileSubheader>
        <TileContentSection>
          <StyledText />
          <StyledHeader id={`social-security-age-label`}>Age</StyledHeader>
          <StyledBenefitHeader id={`social-security-est-benefits-label`}>Estimated Benefit</StyledBenefitHeader>
          <StyledText id={`social-security-you-label`}>You</StyledText>
          <IncrementDecrement
            id={`social-security-partic`}
            value={socialSecurityStartAge}
            helperText="Try Age 70"
            onPlusClick={this.upateSocialSecurityAge(PARTICIPANT, INC)}
            onMinusClick={this.upateSocialSecurityAge(PARTICIPANT, DEC)}
            minValue={62}
            maxValue={70}
          />
          <Input
            name="socialSecurityAmount"
            value={socialSecurityAmount ? formatAmountWithoutDollar(socialSecurityAmount) : ''}
            placeholder="0.00"
            leadingCharacter="$"
            trailingCharacter="/mo."
            onChange={this.handleSocialSecurityAmountChange(PARTICIPANT)}
            autoComplete="off"
            aria-label="Social Security Estimated Benefit"
            width={mq.matches ? 123 : 173}
            textAlign="right"
            hasError={socialSecurityAmountError ? true : false}
            id={`social-security-input-amount`}
          />
        </TileContentSection>
        {socialSecurityAmountError && <StyledError>{socialSecurityAmountError}</StyledError>}

        {hasSpouse && (
          <React.Fragment>
            <StyledDivider />
            <TileContentSection>
              <StyledText id={`social-security-spouse-label`}>Spouse</StyledText>
              <IncrementDecrement
                id={`social-security-spouse`}
                value={spouseSocialSecurityStartAge}
                helperText="Try Age 70"
                minValue={62}
                maxValue={70}
                onPlusClick={this.upateSocialSecurityAge(SPOUSE, INC)}
                onMinusClick={this.upateSocialSecurityAge(SPOUSE, DEC)}
              />
              <Input
                name="spouseSocialSecurityAmount"
                value={spouseSocialSecurityAmount ? formatAmountWithoutDollar(spouseSocialSecurityAmount) : ''}
                placeholder="0.00"
                leadingCharacter="$"
                trailingCharacter="/mo."
                onChange={this.handleSocialSecurityAmountChange(SPOUSE)}
                autoComplete="off"
                aria-label="Social Security Estimated Benefit"
                width={mq.matches ? 123 : 173}
                textAlign="right"
                hasError={spouseSocialSecurityAmountError ? true : false}
                id={`social-security-spouse-input-amount`}
              />
            </TileContentSection>
            {spouseSocialSecurityAmountError && <StyledError>{spouseSocialSecurityAmountError}</StyledError>}
          </React.Fragment>
        )}
      </InputTilePopoverCard>
    );
  }
}

const mapStateToProps = (state: any) => {
  const { socialSecurity } = state;
  return {
    socialSecurity,
    spouseSocialSecurityAdvice: getSpouseSocialSecurityAdvice(state),
    participantSocialSecurityAdvice: getParticipantSocialSecurityAdvice(state),
  };
};

export default connect(mapStateToProps, {
  updateSocialSecurityData: socialSecurityActions.updateSocialSecurityData,
  modelSocialSecurityScenario: socialSecurityActions.modelSocialSecurityScenario,
  updateSocialSecurityError: socialSecurityActions.updateSocialSecurityError,
  hideInputTilePopover: inputTilesActions.hideInputTilePopover,
})(SocialSecurity);
